import EdNumericInput from '@Components/UI/Inputs/EdNumericInput/EdNumericInput';
import RichTextEditor from '@Components/UI/RichTextEditor/RichTextEditor';
import { StudentAnswerHtml } from '@RichTextEditor';
import { FlexLayout } from '@eduact/ed-system';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Divider } from '@mui/material';
import { Typography } from '@mui/material';
import { Paper } from '@mui/material';
import { TinyMCEStyles } from '@Styled/tinymce';
import { Spacer } from '@Styled/utilities';
import React, { useRef } from 'react';
import { Controller, UseFieldArrayUpdate, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { boolean, number, object, ref, string } from 'yup';
import { useSnackbar } from '@Providers/useSnackbar';
import { SubmissionsRequester } from '../Services/Submissions.requester';
import {
	Answers,
	SubmitAttemptQuestionPayload,
} from '../Types/Submissions.types';
import AnswerAttachments from './AnswerAttachments';

type Props = {
	answer: Answers;
	index: number;
	/** View only: inputs disabled, no Save. */
	readOnly?: boolean;
	update: UseFieldArrayUpdate<
		{
			answers: Array<SubmitAttemptQuestionPayload>;
		},
		'answers'
	>;
};

export const AnswerSchema = object({
	question_id: number().required(),
	attempt_id: number().required(),
	score: number()
		.required()
		.min(0, 'Score must be at least 0')
		.max(ref('weight')),
	correct: boolean(),
	weight: number().required(),
	// Optional per-answer feedback; the backend sanitises the HTML on write
	// and the student portal renders it under the answer.
	feedback: string().optional(),
	submitted: boolean().nullable(true).required(),
});
const AttemptQuestion: React.VoidFunctionComponent<Props> = ({
	answer,
	index,
	readOnly = false,
	update,
}) => {
	const { displaySnackbar } = useSnackbar();
	const {
		control,
		formState: { isValid, isDirty },
		handleSubmit,
		reset,
		getValues,
	} = useForm<SubmitAttemptQuestionPayload>({
		resolver: yupResolver(AnswerSchema),
		defaultValues: {
			attempt_id: answer.test_attempt_id,
			question_id: answer.question.id,
			score: answer.score ?? undefined,
			weight: answer.question.weight,
			feedback: answer.feedback ?? '',
			submitted: answer.score !== null && answer.score !== undefined,
		},
		mode: 'onChange',
	});

	// Any edit (score or feedback) marks the row unsaved in the parent form so
	// the attempt cannot be closed on grades that never reached the server.
	// Once per edit session, not per keystroke: every update() re-renders the
	// whole modal and re-validates every answer.
	const unsaved = useRef(false);
	const markUnsaved = () => {
		if (unsaved.current) return;
		unsaved.current = true;
		const values = getValues();
		update(index, {
			...values,
			attempt_id: answer.test_attempt_id,
			question_id: answer.question.id,
			weight: answer.question.weight,
			submitted: null,
		});
	};

	const handleSubmitQuestion = async (data: SubmitAttemptQuestionPayload) => {
		try {
			const response = await SubmissionsRequester.getInstance().submitQuestion(data);
			unsaved.current = false;
			update(index, { ...data, submitted: true });
			reset({ ...data, submitted: true });
			// A closed attempt's total is recomputed with the grade; when a sibling
			// question's weight has changed since it was graded the total is left as
			// it was and the server says which question to redo.
			const warning = response?.data?.data?.warning;
			if (typeof warning === 'string' && warning) displaySnackbar('error', `Grade saved. Total not recomputed: ${warning}`);
		} catch (error: any) {
			// The server refuses a grade entered against a weight that has since
			// changed, because the number would no longer mean what was typed.
			// Swallowing that left the instructor looking at an unchanged form
			// with no sign the save had failed.
			displaySnackbar(
				'error',
				error?.response?.data?.message ?? 'Could not save this grade. Please try again.'
			);
		}
	};
	const studentAnswer = answer.content.options.answer;
	return (
		<AnswerPaper>
			<FlexLayout justifyContent={'space-between'}>
				<Typography fontWeight={'bold'}>Q{answer.question.order}.</Typography>
				<FlexLayout alignItems={'flex-end'}>
					<Typography fontWeight={'500'} color={'GrayText'}>
						Points
					</Typography>
					<Spacer mx={'0.5rem'} />
					<Controller
						control={control}
						name={`score`}
						render={({
							field: { onChange, ...other },
							fieldState: { error },
						}) => {
							return (
								<ResultInput
									onChange={(e) => {
										onChange(e);
										markUnsaved();
									}}
									helperText={error?.message}
									error={!!error}
									disabled={readOnly}
									{...other}
								/>
							);
						}}
					/>

					<Typography fontWeight={700}>/{answer.question.weight}</Typography>
				</FlexLayout>
			</FlexLayout>
			<QuestionContent>
				<Divider />
				<FlexLayout flexDirection={'column'}>
					<span>
						<TinyMCEStyles />
						<QuestionHTML
							dangerouslySetInnerHTML={{ __html: answer.question.content }}
						></QuestionHTML>
					</span>
					<Divider textAlign="left">
						<AnswerLabel>Answer</AnswerLabel>
					</Divider>
					{studentAnswer === null || studentAnswer === undefined || studentAnswer === '' ? (
						<NoAnswer>No answer submitted</NoAnswer>
					) : answer.content.options.type === 'attachment' ? (
						<FlexLayout>
							<AnswerAttachments answer={studentAnswer} />
						</FlexLayout>
					) : (
						<TextAnswer>
							<StudentAnswerHtml html={studentAnswer} />
						</TextAnswer>
					)}
					<Divider textAlign="left">
						<AnswerLabel>Feedback</AnswerLabel>
					</Divider>
					<Controller
						control={control}
						name="feedback"
						render={({ field: { value, onChange, onBlur } }) =>
							readOnly ? (
								<StudentAnswerHtml html={value ?? ''} />
							) : (
								<RichTextEditor
									preset="feedback"
									min_height={0}
									max_height={200}
									value={value ?? ''}
									onChange={(html: string) => {
										onChange(html);
										markUnsaved();
									}}
									onBlur={onBlur}
								/>
							)
						}
					/>
				</FlexLayout>
				{!readOnly && (
					<FlexLayout justifyContent={'flex-end'}>
						<Button
							onClick={handleSubmit(handleSubmitQuestion)}
							disabled={!isValid || !isDirty}
							variant="outlined"
						>
							SUBMIT
						</Button>
					</FlexLayout>
				)}
			</QuestionContent>
		</AnswerPaper>
	);
};

export default AttemptQuestion;

const AnswerPaper = styled(Paper)`
	padding: 2rem;
	margin-bottom: 1.5rem;
`;

const ResultInput = styled(EdNumericInput)`
	.MuiOutlinedInput-input {
		width: 27px;
		height: 27px;
		padding: 0 0.5rem;
	}
`;

const QuestionContent = styled.div`
	padding: 0 3.125rem;
	margin-top: 1.25rem;
`;

const QuestionHTML = styled.div`
	margin: 2rem 0;
	/* Question bodies carry author-uploaded images at their natural size — a
	   phone photo is easily 3000px wide and pushed the grading dialog into
	   horizontal scroll. Constrain to the container; height:auto keeps the
	   aspect ratio. Same for the video embeds and wide tables/code blocks. */
	img,
	video,
	iframe {
		max-width: 100%;
		height: auto;
	}
	table,
	pre {
		max-width: 100%;
		overflow-x: auto;
	}
`;

const AnswerLabel = styled(Typography)`
	font-size: 0.875rem;
	color: #707070;
`;

const TextAnswer = styled.div`
	font-size: 1.125rem;
	white-space: pre-wrap;
`;

const NoAnswer = styled.p`
	color: #a4a4a4;
	font-style: italic;
	margin: 1rem 0;
`;
